/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors, type ValidatorFn } from '@angular/forms';
import { type BaseRequestOptionsArgs, DMValidators, FormControl, HttpResponse, HttpService, IPUtil, Rest } from '@dm/core';
import { type IpWhiteList } from 'app/common/types/mo';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class IpWhiteListService {
    constructor(
        private http: HttpService,
    ) {}

    getBatchRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('ipWhiteList.getBatch');

        if (vstoreId) {
            rest.setParams({ vstoreId });
        }

        return rest;
    }

    getCountRest(cifsShareID: string, vstoreId?: string): Rest {
        const rest = this.http.makeUrl('ipWhiteList.count');

        if (vstoreId) {
            rest.setParams({
                vstoreId,
                cifsShareID,
            });
        }

        return rest;
    }

    /**
     * 批量查询CIFS共享IP规则
     */
    getBatch(cifsShareID: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getBatchRest(vstoreId);
        rest.setFilters([
            { field: 'cifsShareID', matchMode: '::', value: cifsShareID },
        ]);

        return this.http.get(rest, options);
    }

    /**
     * 查询CIFS共享IP规则数量
     */
    getCount(cifsShareID: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getCountRest(cifsShareID, vstoreId);
        return this.http.get(rest, options);
    }

    /**
     * 创建CIFS共享IP规则
     */
    addIpWhiteList(params: IpWhiteList, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('ipWhiteList.post');
        return this.http.post(rest, params, options);
    }

    /**
     * 修改CIFS共享IP规则
     */
    modifyIpWhiteList(id: string, params: IpWhiteList, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('ipWhiteList.put').format(id);
        return this.http.put(rest, params, options);
    }

    /**
     * 移除CIFS共享IP规则
     */
    removeIpWhiteList(id: string, vstoreId: string): Observable<HttpResponse> {
        const rest = this.http.makeUrl('ipWhiteList.delete')
            .format(id)
            .setParams({ vstoreId });

        return this.http.delete(rest);
    }

    /**
     * 获取某个CIFS的IP规则清单
     */
    getExistIps(cifsShareID: string, vstoreId: string, ipList: string[]): void {
        this.getBatch(cifsShareID, vstoreId).subscribe(({ data }) => {
            data.forEach((item) => {
                ipList.push(item.rule);
            });
            this.rulesRepeatValidator(ipList);
        });
    }

    /**
     * 检查IP是否和已有IP重复
     */
    rulesRepeatValidator(ipList: string[], modifiedIp?: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            // 如果当前值没有变化，则不触发重复判断
            if (value === modifiedIp) {
                return null;
            }

            if (!value || !value.length) {
                return null;
            }

            const repeatIpRules = [];
            for (const ip of ipList) {
                if (value === ip) {
                    repeatIpRules.push(value);
                }
            }
            const isValid = repeatIpRules.length > 0;

            return isValid ? { ipRepeatErr: true } : null;
        };
    }

    /**
     * IPv4，IPv6及IP地址段校验
     */
    rulesValidator = (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (!value || !value.length) {
            return null;
        }

        const isSegment = value.includes('-');
        if (!isSegment) {
            return this.checkIp(value) ? null : { ipFormatErr: true };
        }

        if (isSegment) {
            const ipArr = value.split('-');
            if (ipArr.length === 2) {
                // 仅有分隔符没有结束IP
                if (!ipArr[1]) {
                    return { ipFormatErr: true };
                }

                // 开始IP有“/”或结束IP没有“/”
                if (ipArr[0].includes('/') || !ipArr[1].includes('/')) {
                    return { ipFormatErr: true };
                }

                // 校验IP段中的两个IP是否为同一类型
                if (IPUtil.getType(ipArr[0]) !== IPUtil.getType(ipArr[1])) {
                    return { ipTypeErr: true };
                }

                // 校验开始和结束IP的格式
                if (!this.checkIp(ipArr[0]) || !this.checkIp(ipArr[1])) {
                    return { ipFormatErr: true };
                }

                // 校验IP范围是否合理，结束IP必须大于开始IP
                const onlyIp = this.getIp(ipArr[1]);
                if (IPUtil.compare(ipArr[0], onlyIp) >= 0) {
                    return { ipRangeErr: true };
                }
            } else {
                return { ipFormatErr: true };
            }
        }

        return null;
    };

    /**
     * 检查IP格式，校验通过返回true
     */
    checkIp(ip: string): boolean {
        const isIpv4 = IPUtil.getType(ip) === 'IPV4';
        const hasSubnetOrPrefix = ip.includes('/');
        if (hasSubnetOrPrefix) {
            const onlyIp = this.getIp(ip);
            if (!onlyIp) {
                return false;
            }
            const subnetOrPrefix = this.getSubnetOrPrefix(ip);
            const isValid = this.checkIpFormat(onlyIp) && this.checkSubnetOrPrefix(subnetOrPrefix, isIpv4);

            return isValid;
        }

        const isIpValid = !hasSubnetOrPrefix && this.checkIpFormat(ip);

        return isIpValid;
    }

    /**
     * 获取纯ip
     */
    getIp(ip: string): string {
        const index = ip.indexOf('/');
        return ip.slice(0, index);
    }

    /**
     * 获取子网掩码/前缀
     */
    getSubnetOrPrefix(ip: string): string {
        const index = ip.indexOf('/');
        return ip.slice(index + 1);
    }

    /**
     * 同时验证IPV4 IPV6合法性
     */
    checkIpFormat(ip: string): boolean {
        const ipControl = new FormControl(ip);
        if (DMValidators.ipWithMulticast(ipControl)) {
            return false;
        }

        return true;
    }

    /**
     * 检查子网掩码和前缀是否在范围内
     */
    checkSubnetOrPrefix(subnetOrPrefix: string, isIpv4: boolean): boolean {
        if (isIpv4) {
            const IPV4_SUBNET_REGEXP = /^([1-9]|[1-2]\d|3[0-2])$/g;
            return IPV4_SUBNET_REGEXP.test(subnetOrPrefix);
        } else {
            const IPV6_PREFIX_REGEXP = /^([1-9]|[1-9]\d|1[0-1]\d|12[0-8])$/g;
            return IPV6_PREFIX_REGEXP.test(subnetOrPrefix);
        }
    }
}
